import { labelStatus, labelText } from '@crm/utils'
import { ConditionalText, DayLabel } from '@shared/components'

import { Button, Card, Col, Flex, Row, Tag, theme, Tooltip } from 'antd'

import {
  Category,
  Clock,
  InfoCircle,
  Maximize4,
  Profile2User,
  TableDocument
} from 'iconsax-react'
import { type MouseEvent, memo } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

import type { TaskKanbanReadable } from '@crm/api'

import type { CardProps } from 'antd/lib'

const ONE_DAY = 24 * 60 * 60 * 1000

type Props = {
  data?: TaskKanbanReadable
  onEdit?: () => void
} & CardProps

export const TasksKanbanCard = memo(
  ({ data, onEdit: onEditCb, ...props }: Props) => {
    const { t } = useTranslation()
    const { styles } = useStyles()
    const { token } = theme.useToken()

    const onEdit = (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      onEditCb?.()
    }
    const deadline = new Date(String(data?.deadline)).getTime()
    const now = Date.now()

    const isExpired = now > deadline
    const isOneDayBeforeDeadline = !isExpired && deadline - now <= ONE_DAY

    return (
      <Card className={styles.card} {...props}>
        <Flex vertical gap={8} className={styles.cardContent}>
          <Row align="middle" gutter={4}>
            <Col span={21}>
              <Flex align="center" gap={8}>
                <Tooltip title={t('accountSettings.title')} placement="left">
                  <Flex align="center" gap={8}>
                    <TableDocument size={16} color={token.colorIcon} />

                    <ConditionalText
                      condition={!!data?.title}
                      isEllipsis
                      weight={500}
                      className={styles.titleMaxWidth}
                    >
                      {data?.title}
                    </ConditionalText>
                  </Flex>
                </Tooltip>

                {data?.priority && (
                  <Tooltip title={t('task.priority')}>
                    <Tag
                      color={labelStatus[data?.priority] ?? 'default'}
                      className={styles.noMarginRight}
                    >
                      {t(labelText[data?.priority])}
                    </Tag>
                  </Tooltip>
                )}
              </Flex>
            </Col>

            <Col span={3}>
              <Tooltip title={t('task.edit')}>
                <Button
                  className={styles.expandButton}
                  icon={<Maximize4 size={14} color={token.colorIcon} />}
                  type="text"
                  size="small"
                  onClick={onEdit}
                />
              </Tooltip>
            </Col>
          </Row>

          <Tooltip title={t('billing.deadline')} placement="left">
            <Flex align="center" gap={8}>
              <Flex>
                <Clock
                  size={16}
                  color={isExpired ? token.red5 : token.colorIcon}
                />
              </Flex>

              <DayLabel
                date={data?.deadline}
                textProps={{
                  className: isExpired
                    ? styles.deadLineExpired
                    : styles.deadLine
                }}
              />

              {isExpired && (
                <Tooltip title={t('logs.expired')}>
                  <InfoCircle size={16} color={token.red5} />
                </Tooltip>
              )}

              {!isExpired && isOneDayBeforeDeadline && (
                <Tooltip title={t('task.oneDayBeforeDeadline')}>
                  <InfoCircle size={16} color={token.yellow6} />
                </Tooltip>
              )}
            </Flex>
          </Tooltip>

          <Tooltip title={t('lead.leadOwner')} placement="left">
            <Flex align="center" gap={8}>
              <Profile2User size={16} color={token.colorIcon} />

              <ConditionalText
                condition={!!data?.owner?.name}
                isEllipsis
                weight={500}
              >
                {data?.owner?.name}
              </ConditionalText>
            </Flex>
          </Tooltip>

          <Tooltip title={t('filter.type')} placement="left">
            <Flex align="center" gap={8}>
              <Category size={16} color={token.colorIcon} />

              <ConditionalText
                condition={!!data?.type?.name}
                isEllipsis
                weight={500}
              >
                {data?.type?.name}
              </ConditionalText>
            </Flex>
          </Tooltip>
        </Flex>
      </Card>
    )
  }
)
